import React, { useEffect, useState } from "react";
import { Flex } from "rebass/styled-components";

import "../../../../../corewellhealth/src/styles/typography.scss";
import { corewellHeroOverrideClasses, layout } from "../../../../constants/constants";
import { Media } from "../../../contexts/media/MediaContext";
import { TransactionalHeroContent, IconPack, Icon, FileContent, FeatureToggle } from "../../../models/app-data-model";
import ShIcon from "../../sh-icon/sh-icon";
import ShLink from "../../sh-link/sh-link";
import "./transactional-hero.scss";

export type transactionalHeroProps = {
  content: TransactionalHeroContent;
  iconPack?: IconPack[];
  subsiteNavPresent: boolean;
  headerPresent?: boolean;
  featureToggles: FeatureToggle[];
};

const TransactionalHero: React.FC<transactionalHeroProps> = ({
  content,
  iconPack,
  headerPresent,
  featureToggles
}: transactionalHeroProps) => {
  const [scrollToElement, setScrollToElement] = useState<HTMLDivElement>();
  const [ctaOneHover, setCtaOneHover] = useState(false);
  const [ctaTwoHover, setCtaTwoHover] = useState(false);
  const [ctaThreeHover, setCtaThreeHover] = useState(false);

  useEffect(() => {
    if (typeof document !== "undefined" && document.getElementById("transactional-hero-container")) {
      setScrollToElement(document.getElementById("scroll-to") as HTMLDivElement);
    }
  }, []);

  const iconMock = (iconSource: FileContent) => {
    return {
      svg: {
        content_type: "image/svg+xml",
        localAsset: iconSource.localAsset,
        title: iconSource.title,
        url: iconSource.url
      }
    } as Icon;
  };

  const addSpanToWords = (words: string) => {
    const result = words
      .split(" ")
      .map((word) => (
        <span className={`text-highlight-span ${content?.text_highlight_color?.toLowerCase()}-background`}>{`${word} `}</span>
      ));
    return result;
  };

  const ctaHoverState = (ctaIndex: number, hovering: boolean): void => {
    if (ctaIndex === 0) {
      setCtaOneHover(hovering);
    } else if (ctaIndex === 1) {
      setCtaTwoHover(hovering);
    } else if (ctaIndex === 2) {
      setCtaThreeHover(hovering);
    }
  };

  const noMarginTopClass = !headerPresent ? "no-margin-top" : "";
  const transactionalHeroClasses = `transactional-hero ${noMarginTopClass} ch-hero-margin-top-override`;

  const transactionalHeroContainerClasses = `transactional-hero-container ${
    !content?.sub_headline_text && content?.cta_buttons?.length === 0 ? "no-links-or-ctas" : ""
  }`;

  const renderTransactionalHero = (image: any) => {
    return (
      <div className={transactionalHeroClasses} data-testid="transactional-hero">
        <div className={transactionalHeroContainerClasses} id="transactional-hero-container" style={{ backgroundImage: `url(${image})` }}>
          {content?.header && content.hide_header_text === false && (
            <Flex width={layout.pageWidth} px={layout.pagePaddingX} marginX={"auto"} flexDirection={"column"}>
              <h1 className={`xl-6 transactional-hero-header ${content.hide_header_text ? "hidden" : ""}`}>{addSpanToWords(content.header)}</h1>
            </Flex>
          )}
          {content.hide_header_text && (
            <h1 data-testid="hidden-hero-header" className="hidden xl-6">
              {content.header}
            </h1>
          )}
          {(content?.sub_headline_text || content?.cta_buttons?.length !== 0) && (
            <div className="lower-blue-background">
              <Flex
                className="transactional-hero-quick-actions"
                width={layout.pageWidth}
                marginX={"auto"}
                flexDirection={"column"}
                data-testid="transactional-hero-quick-actions"
              >
                {content?.sub_headline_text && (
                  <div className="hero-subheadline-container">
                    <div
                      className="rich-text transactional-hero-subheadline-text large-text"
                      dangerouslySetInnerHTML={{ __html: content?.sub_headline_text }}
                    ></div>
                  </div>
                )}
                <div className="quick-actions">
                  {content?.cta_buttons?.map((quickAction, index) => {
                    return (
                      <div
                        onMouseEnter={() => ctaHoverState(index, true)}
                        onClick={() => ctaHoverState(index, false)}
                        onMouseLeave={() => ctaHoverState(index, false)}
                        className={`qa-cta-container ${quickAction.button_subheadline ? "" : "button-subheadline-blank"}`}
                        data-testid="qa-cta"
                      >
                        <ShLink
                          className={`quick-action-cta ${quickAction.button_subheadline ? "subheadline" : "no-subheadline"}`}
                          reference={quickAction.reference}
                          key={`cta-link-${index}`}
                          featureToggles={featureToggles}
                          iconPack={iconPack}
                        >
                          <div
                            className={`quick-action-icon-and-button-headline ${
                              quickAction.button_subheadline ? "" : "button-subheadline-none"
                            }`}
                          >
                            {quickAction.icon_source && (
                              <ShIcon className="quick-action-cta-icon" icon={iconMock(quickAction.icon_source)} />
                            )}
                            {quickAction.button_headline && (
                              <div className="quick-action-button-headline large-text">{quickAction.button_headline}</div>
                            )}
                          </div>
                          <div className={`subheadline-plus-arrow ${quickAction.button_subheadline ? "" : "no-button-subheadline"}`}>
                            {quickAction.button_subheadline && (
                              <Media greaterThanOrEqual="desktop">
                                <p className="quick-action-button-subheadline extra-small-text ">{quickAction.button_subheadline}</p>
                              </Media>
                            )}
                            <ShIcon
                              iconPack={iconPack}
                              iconName={
                                (index === 0 && ctaOneHover) || (index === 1 && ctaTwoHover) || (index === 2 && ctaThreeHover)
                                  ? "dark_button_arrow"
                                  : "button_arrow"
                              }
                              className="icon quick-action-button-arrow-icon"
                            />
                          </div>
                        </ShLink>
                        <div
                          className="hover-div"
                          key={`hover-div-${index}`}
                          data-testid={ctaOneHover || ctaTwoHover || ctaThreeHover ? "hovering-true" : "hovering-false"}
                        ></div>
                      </div>
                    );
                  })}
                </div>
              </Flex>
            </div>
          )}
        </div>
        <div id="scroll-to" className="scroll-to"></div>
      </div>
    );
  };

  return (
    <>
      <Media greaterThanOrEqual="tablet">{renderTransactionalHero(content?.image_desktop?.file?.url)}</Media>
      <Media lessThan="tablet">{renderTransactionalHero(content?.image_mobile?.file?.url)}</Media>
    </>
  );
};

export default TransactionalHero;
